import {gql} from '@apollo/client';
import {DocumentNode} from 'graphql';
import React from 'react';

import {
  JellyfishViewQuery,
  JellyfishViewQueryVariables,
} from 'app-core/archetype/workspace/__generated/JellyfishViewQuery';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import JellyfishDetails from './JellyfishDetails';

interface JellyfishViewProps {
  id: string;
}

export const JELLYFISH_VIEW_QUERY: DocumentNode = gql`
  query JellyfishViewQuery($id: StrictString!) @skipAudit @engComponent(name: CORE_OBJECT) {
    jellyfish(id: $id) {
      ...JellyfishFileStateGraphQL
      ...StarrableGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('JellyfishFileState').fragment}
  ${getFragmentWrapper('Starrable').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export default function JellyfishView({id}: JellyfishViewProps): JSX.Element | null {
  const result = useBenchlingQuery<JellyfishViewQuery, JellyfishViewQueryVariables>(JELLYFISH_VIEW_QUERY, {
    variables: {id},
  });

  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.SUCCESS) {
    const {data} = result;
    if (isInaccessible(data.jellyfish)) {
      return (
        <WorkspaceItemInaccessibleGuard
          inaccessibleInfo={data.jellyfish}
          modelType={ModelTypeConstants.JELLYFISH}
        />
      );
    } else {
      const reload = (): void => {
        result.refetch({id});
      };
      return <JellyfishDetails jellyfish={data.jellyfish} reload={reload} />;
    }
  } else {
    return null;
  }
}
